const STORAGE_PREFIX = 'soundscreen.enabledPlugins';
const PLUGIN_PREFERENCES_EVENT = 'soundscreen:plugin-preferences-changed';

const getStorageKey = (siteId) => `${STORAGE_PREFIX}.${siteId || 'global'}`;

const readEnabledPluginKeys = (siteId) => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(getStorageKey(siteId)) || '[]');
    return Array.isArray(parsed)
      ? parsed.map((item) => String(item || '').trim()).filter(Boolean)
      : [];
  } catch (_error) {
    return [];
  }
};

export const getEnabledPluginKeys = (siteId) => readEnabledPluginKeys(siteId);

export const isPluginEnabled = (siteId, pluginKey) =>
  readEnabledPluginKeys(siteId).includes(String(pluginKey || '').trim());

export const setPluginEnabled = (siteId, pluginKey, enabled) => {
  if (typeof window === 'undefined') return [];
  const normalizedPluginKey = String(pluginKey || '').trim();
  if (!normalizedPluginKey) return readEnabledPluginKeys(siteId);

  const current = readEnabledPluginKeys(siteId);
  const next = enabled
    ? Array.from(new Set([...current, normalizedPluginKey]))
    : current.filter((item) => item !== normalizedPluginKey);

  window.localStorage.setItem(getStorageKey(siteId), JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(PLUGIN_PREFERENCES_EVENT, {
    detail: { siteId, enabledPluginKeys: next },
  }));
  return next;
};

const getAuthHeaders = () => {
  if (typeof window === 'undefined') return {};
  const token = window.localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const cacheEnabledPluginKeys = (siteId, enabledPluginKeys = []) => {
  if (typeof window === 'undefined') return [];
  const next = Array.isArray(enabledPluginKeys)
    ? enabledPluginKeys.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  window.localStorage.setItem(getStorageKey(siteId), JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(PLUGIN_PREFERENCES_EVENT, {
    detail: { siteId, enabledPluginKeys: next },
  }));
  return next;
};

export const loadEnabledPluginKeys = async (siteId) => {
  if (!siteId || typeof window === 'undefined') return readEnabledPluginKeys(siteId);

  try {
    const response = await window.fetch(`/api/sites/${siteId}/plugins`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to load plugin settings');
    const data = await response.json();
    return cacheEnabledPluginKeys(siteId, data?.enabledPluginKeys || []);
  } catch (_error) {
    return readEnabledPluginKeys(siteId);
  }
};

export const persistPluginEnabled = async (siteId, pluginKey, enabled) => {
  const previous = readEnabledPluginKeys(siteId);
  const optimistic = setPluginEnabled(siteId, pluginKey, enabled);
  if (!siteId || typeof window === 'undefined') return optimistic;

  try {
    const response = await window.fetch(`/api/sites/${siteId}/plugins/${encodeURIComponent(pluginKey)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ enabled: Boolean(enabled) }),
    });
    if (!response.ok) throw new Error('Failed to save plugin settings');
    const data = await response.json();
    return cacheEnabledPluginKeys(siteId, data?.enabledPluginKeys || optimistic);
  } catch (error) {
    cacheEnabledPluginKeys(siteId, previous);
    throw error;
  }
};

export const subscribeToPluginPreferences = (callback) => {
  if (typeof window === 'undefined' || typeof callback !== 'function') return () => {};

  const handleChange = (event) => callback(event);
  window.addEventListener(PLUGIN_PREFERENCES_EVENT, handleChange);
  window.addEventListener('storage', handleChange);

  return () => {
    window.removeEventListener(PLUGIN_PREFERENCES_EVENT, handleChange);
    window.removeEventListener('storage', handleChange);
  };
};
